import DropdownMenu from '@/common/components/DropdownMenu'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { AnticipateGovernmentAllocationModal } from '@/features/privateCompanies/components/AnticipateGovernmentAllocationModal'
import { ExtendGovernmentAllocationModal } from '@/features/privateCompanies/components/ExtendGovernmentAllocationModal'
import { ResetGovernmentAllocationDistributionModal } from '@/features/privateCompanies/components/ResetGovernmentAllocationRemainingDistributionModal'
import { UpdateGovernmentAllocationApprovedValueModal } from '@/features/privateCompanies/components/UpdateGovernmentAllocationApprovedValueModal'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { useMemo, useState } from 'react'
import { CreateGovernmentAllocationAnnulativeEntryModal } from '../CreateGovernmentAllocationAnnulativeEntryModal'
import { isAfter } from 'date-fns'
import { useCompanyGovernmentAllocationEntries } from '../../hooks/useCompanyGovernmentAllocationEntries'
import { AnticipateEndDateGovernmentAllocationModal } from '../AnticipateEndDateGovernmentAllocationModal'
import { DeleteGovernmentAllocationModal } from '@/features/privateCompanies/components/DeleteGovernmentAllocationModal'

interface GovernmentAllocationDropdownProps {
  companyId: string
  defaultOpen?: boolean
  companyGovernmentAllocation: CompanyGovernmentAllocationData
}

export const GovernmentAllocationDropdown = ({
  companyId,
  defaultOpen = false,
  companyGovernmentAllocation,
}: GovernmentAllocationDropdownProps) => {
  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToChangeApprovedValue = hasUserPermission(
    'Admin::Billing::GovernmentAllocations::ChangeApprovedValue',
  )
  const userHasPermissionToResetRemainingDistributions = hasUserPermission(
    'Admin::Billing::GovernmentAllocations::ResetRemainingDistributions',
  )
  const userHasPermissionToAnticipateGovernmentAllocation = hasUserPermission(
    'Admin::Billing::GovernmentAllocations::Anticipate',
  )
  const userHasPermissionToExtendGovernmentAllocation = hasUserPermission(
    'Admin::Billing::GovernmentAllocations::Extend',
  )
  const userHasPermissionToAnticipateEndDateGovernmentAllocation = hasUserPermission(
    'Admin::Billing::GovernmentAllocations::AnticipateEndDate',
  )
  const userHasPermissionToDeleteGovernmentAllocation = hasUserPermission(
    'Admin::Billing::GovernmentAllocations::Destroy',
  )

  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(defaultOpen)
  const [isZeroDistributionOpen, setIsZeroDistributionOpen] = useState(false)
  const [isExtendGovernmentAllocationModalOpen, setIsExtendGovernmentAllocationModalOpen] = useState(false)
  const [isAnticipateGovernmentAllocationModalOpen, setIsAnticipateGovernmentAllocationModalOpen] = useState(false)
  const [isAnticipateEndDateGovernmentAllocationModalOpen, setIsAnticipateEndDateGovernmentAllocationModalOpen] = useState(false)
  const [isCreateAnnulativeEntryModalOpen, setIsCreateAnnulativeEntryModalOpen] = useState(false)
  const [isDeleteGovernmentAllocationModalOpen, setIsDeleteGovernmentAllocationModalOpen] = useState(false)

  const { data: entriesData } = useCompanyGovernmentAllocationEntries<Array<{ kind: string, status: string }>>({
    governmentAllocationId: companyGovernmentAllocation.id,
    filters: { page: 1 },
  })

  const hasAnnulativeEntry = entriesData?.some((entry) => entry.kind === 'annulative')

  const isExpired = useMemo(() => {
    return companyGovernmentAllocation
      ? isAfter(new Date(), new Date(companyGovernmentAllocation?.applicable_end_date))
      : false
  }, [companyGovernmentAllocation?.applicable_end_date])

  return (
    <>
      <DropdownMenu
        dataTestId="government_allocation_options_dropdown_trigger"
        triggerLabel="Opções do empenho"
        itemClassName="min-w-[200px]"
        disabled={isUserPermissionsPending}
        options={[
          ...((!isExpired) ? [{
            label: 'Alterar valor aprovado',
            onClick: () => {
              setIsAllocationModalOpen(true)
            },
            disabled: !userHasPermissionToChangeApprovedValue,
            dataTestId: 'change_approve_value_modal_trigger',
          }] : []),
          ...((!isExpired) ? [{
            label: 'Zerar distribuições',
            onClick: () => {
              setIsZeroDistributionOpen(true)
            },
            disabled: !userHasPermissionToResetRemainingDistributions,
          }] : []),
          {
            label: 'Antecipar período',
            onClick: () => {
              setIsAnticipateGovernmentAllocationModalOpen(true)
            },
            disabled: !userHasPermissionToAnticipateGovernmentAllocation,
          },
          {
            label: 'Estender período',
            onClick: () => {
              setIsExtendGovernmentAllocationModalOpen(true)
            },
            disabled: !userHasPermissionToExtendGovernmentAllocation,
          },
          {
            label: 'Antecipar data final',
            onClick: () => {
              setIsAnticipateEndDateGovernmentAllocationModalOpen(true)
            },
            disabled: !userHasPermissionToAnticipateEndDateGovernmentAllocation,
          },
          ...((isExpired && !hasAnnulativeEntry) ? [{
            label: 'Solicitar anulação de saldo',
            onClick: () => {
              setIsCreateAnnulativeEntryModalOpen(true)
            },
          }] : []),
          {
            label: 'Excluir empenho',
            onClick: () => {
              setIsDeleteGovernmentAllocationModalOpen(true)
            },
            disabled: !userHasPermissionToDeleteGovernmentAllocation,
          },
        ]}
      />

      <UpdateGovernmentAllocationApprovedValueModal
        isOpen={isAllocationModalOpen}
        setIsOpen={setIsAllocationModalOpen}
        companyGovernmentAllocation={companyGovernmentAllocation}
      />

      <ResetGovernmentAllocationDistributionModal
        isOpen={isZeroDistributionOpen}
        setIsOpen={setIsZeroDistributionOpen}
      />

      <AnticipateGovernmentAllocationModal
        isOpen={isAnticipateGovernmentAllocationModalOpen}
        setIsOpen={setIsAnticipateGovernmentAllocationModalOpen}
        companyGovernmentAllocation={companyGovernmentAllocation}
      />

      <AnticipateEndDateGovernmentAllocationModal
        isOpen={isAnticipateEndDateGovernmentAllocationModalOpen}
        setIsOpen={setIsAnticipateEndDateGovernmentAllocationModalOpen}
        companyGovernmentAllocation={companyGovernmentAllocation}
      />

      <ExtendGovernmentAllocationModal
        isOpen={isExtendGovernmentAllocationModalOpen}
        setIsOpen={setIsExtendGovernmentAllocationModalOpen}
        companyGovernmentAllocation={companyGovernmentAllocation}
      />

      <CreateGovernmentAllocationAnnulativeEntryModal
        isOpen={isCreateAnnulativeEntryModalOpen}
        setIsOpen={setIsCreateAnnulativeEntryModalOpen}
        companyGovernmentAllocation={companyGovernmentAllocation}
      />

      <DeleteGovernmentAllocationModal
        companyId={companyId}
        isOpen={isDeleteGovernmentAllocationModalOpen}
        setIsOpen={setIsDeleteGovernmentAllocationModalOpen}
      />
    </>
  )
}
